import React, {useState, KeyboardEvent} from 'react';
import {Button, SpaceBetween, Textarea, Box} from '@amzn/awsui-components-react';

export interface ChatInputProps {
    /** Callback when user sends a message */
    onSendMessage: (message: string) => void;
    /** Callback when user starts a new conversation */
    onNewConversation: () => void;
    /** Whether the input should be disabled (e.g., during API call) */
    disabled: boolean;
}

/**
 * ChatInput component provides the user interface for entering and sending messages
 * to the chatbot, as well as starting new conversations.
 *
 * Requirements:
 * - 2.1: Enable send button when input is non-empty
 * - 2.2: Submit message on Enter or button click, add to history, clear input
 * - 2.3: Prevent submission of empty/whitespace messages
 * - 4.1: Provide new conversation button
 */
export function ChatInput(props: ChatInputProps) {
    const [inputValue, setInputValue] = useState('');

    /**
     * Checks if the input is empty or contains only whitespace
     */
    const isInputEmpty = (): boolean => {
        return inputValue.trim().length === 0;
    };

    /**
     * Handles message submission
     */
    const handleSendMessage = () => {
        // Requirement 2.3: Prevent submission of empty/whitespace messages
        if (isInputEmpty()) {
            return;
        }

        // Requirement 2.2: Submit message and clear input
        props.onSendMessage(inputValue);
        setInputValue('');
    };

    /**
     * Handles key down for message submission
     */
    const handleKeyDown = (event: any) => {
        // Submit on Enter (without Shift)
        if (event.detail.key === 'Enter' && !event.detail.shiftKey) {
            event.preventDefault();
            handleSendMessage();
        }
    };

    /**
     * Handles new conversation button click
     */
    const handleNewConversation = () => {
        props.onNewConversation();
        setInputValue('');
    };

    return (
        <div className="chat-input-container">
            <SpaceBetween size="s" direction="vertical">
                <Textarea
                    value={inputValue}
                    onChange={(event) => setInputValue(event.detail.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your message here... (Press Enter to send, Shift+Enter for new line)"
                    disabled={props.disabled}
                    rows={3}
                    data-testid="chat-input-textarea"
                />
                <Box float="right">
                    <SpaceBetween size="xs" direction="horizontal">
                        <Button
                            variant="link"
                            onClick={handleNewConversation}
                            disabled={props.disabled}
                            data-testid="new-conversation-button"
                            iconName="refresh"
                        >
                            New Conversation
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSendMessage}
                            disabled={props.disabled || isInputEmpty()}
                            data-testid="send-button"
                            iconName="angle-right-double"
                            iconAlign="right"
                        >
                            Send
                        </Button>
                    </SpaceBetween>
                </Box>
            </SpaceBetween>
        </div>
    );
}
